import { VIMEO_SHOWCASE_URL } from '../data/fixer-content.js'
import { useFixerContent } from '../hooks/i18n/usePageContent.js'
import { FixerCard, FixerKicker, FixerSection, FixerSectionIntro, FixerSignalDivider } from './fixer-ui.js'

export function FixerShowcaseSection() {
  const c = useFixerContent()
  const featured = c.showcase.featured

  return (
    <FixerSection id="showcase">
      <div className="fixer-stack-xl">
        <FixerSectionIntro
          channel="vision"
          kicker={c.showcase.number}
          title={c.showcase.title}
          copy={c.showcase.copy}
          titleId="fixer-showcase-heading"
        />

        <a
          href={featured.videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="fixer-showcase-feature group block"
          aria-label={c.showcase.watchAria.replace('{{title}}', featured.projectTitle)}
        >
          <div className="relative aspect-video w-full">
            <img
              src={featured.thumbUrl}
              alt={c.showcase.thumbAlt.replace('{{title}}', featured.projectTitle)}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/10"
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              aria-hidden="true"
            >
              <span
                className="flex h-14 w-14 items-center justify-center rounded-sm border border-white/40 bg-black/35 backdrop-blur-sm"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-2 p-6 md:p-8">
              <FixerKicker channel="vision" className="text-white/70">{featured.role}</FixerKicker>
              <h3 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
                {featured.projectTitle}
              </h3>
              <p className="text-sm font-medium text-[var(--fc-accent-soft)]">{featured.views}</p>
            </div>
          </div>
        </a>

        <div className="grid grid-cols-1 gap-6 pt-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-10">
          <p className="fixer-body-sm max-w-2xl">{featured.description}</p>
          <a href={featured.videoUrl} target="_blank" rel="noopener noreferrer" className="fixer-link-accent inline-flex items-center gap-2">
            {featured.watchLabel}
            <span aria-hidden="true">→</span>
          </a>
        </div>

        <FixerSignalDivider className="my-8" />

        <div className="fixer-case-credits" aria-label={c.showcase.creditsAria}>
          {c.caseCredits.map(credit => (
            <FixerCard key={credit.project} interactive>
              <div className="fixer-stack-md">
                <FixerKicker channel="vision" active={false}>{credit.role}</FixerKicker>
                <h3 className="fixer-subheading">{credit.project}</h3>
                <p className="fixer-body-sm">{credit.note}</p>
              </div>
            </FixerCard>
          ))}
        </div>

        <FixerSignalDivider className="my-8" />

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
          <div className="fixer-stack-md lg:w-64 lg:shrink-0">
            <FixerKicker channel="vision">{c.showcase.reel.title}</FixerKicker>
            <p className="fixer-body-sm">{c.showcase.reel.copy}</p>
          </div>
          <div className="min-w-0 flex-1 fixer-showcase-embed" aria-label={c.chrome.showcaseAria}>
            <iframe
              src={VIMEO_SHOWCASE_URL}
              title={c.chrome.showcaseAria}
              allow="autoplay; fullscreen; picture-in-picture; gyroscope; accelerometer; clipboard-write; encrypted-media; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>
      </div>
    </FixerSection>
  )
}
